import { QRCodeSVG } from 'qrcode.react';

// The printed invoice/dispatch document: a fixed A4 sheet (210mm x 297mm).
//
// Extracted from InvoiceCreateForm's "Преглед" mode so the create form, the
// invoice view page and the standalone preview tab all render one document and
// cannot drift apart. The sheet must keep its fixed mm size to print correctly,
// so callers are responsible for wrapping it in an overflow-x-auto container
// when it sits in a column narrower than 210mm.
//
// `invoice` uses the create form's shape; API data goes through
// utils/invoiceToDocument.js first.
const InvoiceA4Document = ({
  invoice,
  businessDetails = {},
  documentType = 'invoice',
  invoiceType = 'outgoing',
  transport = {},
}) => {
  const items = invoice.items || [];

  // Same arithmetic as the create form: a flat 18% VAT on the line-item sum.
  const subtotal = items.reduce(
    (sum, item) => sum + parseFloat(item.quantity || 0) * parseFloat(item.price || 0),
    0
  );
  const vat = subtotal * 0.18;
  const total = subtotal + vat;

  return (
    <div
      className={`bg-white shadow-lg border-t-4 p-8 mb-6 mx-auto flex flex-col ${
        documentType === 'invoice' ? 'border-green-600' : 'border-green-300'
      }`}
      style={{ width: '210mm', minHeight: '297mm' }}
    >
      {/* Logo and Company Header */}
      <div className="flex justify-between items-start mb-8">
        <div className="flex items-center space-x-3">
          <img src="/images/logo.svg" alt="Logo" className="w-12 h-12" />
          <h1 className="text-2xl font-bold text-gray-900">{businessDetails.name || 'ЕЛКОС'}</h1>
        </div>
        <div className="text-right">
          <h2 className="text-2xl font-bold text-gray-900 tracking-wide">
            {documentType === 'invoice' ? 'ФАКТУРА' : 'ИСПРАТНИЦА'} {invoice.invoiceNumber}
          </h2>
          <div className="text-sm text-gray-500 mt-2">Датум</div>
          <div className="text-gray-900">
            {(() => {
              if (!invoice.date) return '';
              const [year, month, day] = invoice.date.split('-');
              return `${day}/${month}/${year}`;
            })()}
          </div>
        </div>
      </div>

      {/* From / To Section */}
      <div className="grid grid-cols-2 gap-8 mb-8">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">ОД</div>
          <div className={`border-l-4 pl-3 ${
            documentType === 'invoice' ? 'border-green-600' : 'border-green-300'
          }`}>
            <div className="font-semibold text-gray-900">
              {invoiceType === 'outgoing' ? businessDetails.name : invoice.fromCompanyName || '-'}
            </div>
            {invoiceType === 'outgoing' && businessDetails.address && (
              <div className="text-sm text-gray-600">{businessDetails.address}</div>
            )}
            {invoiceType === 'outgoing' && businessDetails.phone && (
              <div className="text-sm text-gray-600">Тел: {businessDetails.phone}</div>
            )}
            {invoiceType === 'outgoing' && businessDetails.bank_account && (
              <div className="text-sm text-gray-600">
                Жиро сметка: {businessDetails.bank_account}{businessDetails.bank_name && ` ${businessDetails.bank_name}`}
              </div>
            )}
            {invoiceType === 'outgoing' && businessDetails.tax_id && (
              <div className="text-sm text-gray-600">ЕДБ: {businessDetails.tax_id}</div>
            )}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">ДО</div>
          <div className="border-l-4 border-gray-300 pl-3">
            <div className="font-semibold text-gray-900">
              {invoiceType === 'outgoing' ? invoice.toCompanyName : businessDetails.name || '-'}
            </div>
            {invoiceType === 'outgoing' && invoice.toCompanyAddress && (
              <div className="text-sm text-gray-600">{invoice.toCompanyAddress}</div>
            )}
            {invoiceType === 'outgoing' && invoice.toCompanyPhone && (
              <div className="text-sm text-gray-600">Тел: {invoice.toCompanyPhone}</div>
            )}
            {invoiceType === 'incoming' && businessDetails.address && (
              <div className="text-sm text-gray-600">{businessDetails.address}</div>
            )}
            {invoiceType === 'incoming' && businessDetails.phone && (
              <div className="text-sm text-gray-600">Тел: {businessDetails.phone}</div>
            )}
            {invoiceType === 'incoming' && businessDetails.bank_account && (
              <div className="text-sm text-gray-600">Жиро сметка: {businessDetails.bank_account}</div>
            )}
          </div>
        </div>
      </div>

      {/* Transport Details - Only for Dispatch Preview */}
      {documentType === 'dispatch' && (transport.dispatchAddress || transport.vehicleRegistration || transport.driverName) && (
        <div className="mb-8 border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">ТРАНСПОРТ</div>
          <div className="grid grid-cols-3 gap-4">
            {transport.dispatchAddress && (
              <div>
                <div className="text-xs text-gray-500">Адреса</div>
                <div className="text-sm font-medium text-gray-900">{transport.dispatchAddress}</div>
              </div>
            )}
            {transport.vehicleRegistration && (
              <div>
                <div className="text-xs text-gray-500">Возило</div>
                <div className="text-sm font-medium text-gray-900">{transport.vehicleRegistration}</div>
              </div>
            )}
            {transport.driverName && (
              <div>
                <div className="text-xs text-gray-500">Подигнал</div>
                <div className="text-sm font-medium text-gray-900">{transport.driverName}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Items Table */}
      <div className="mb-8">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left py-3 text-sm font-medium text-gray-600">#</th>
              <th className="text-left py-3 text-sm font-medium text-gray-600">ОПИС</th>
              <th className="text-center py-3 text-sm font-medium text-gray-600">КОЛ.</th>
              <th className="text-center py-3 text-sm font-medium text-gray-600">ЕД.</th>
              {documentType === 'invoice' && (
                <>
                  <th className="text-right py-3 text-sm font-medium text-gray-600">ЦЕНА</th>
                  <th className="text-right py-3 text-sm font-medium text-gray-600">ВКУПНО</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {items.filter(item => item.name).map((item, index) => {
              const itemTotal = parseFloat(item.quantity || 0) * parseFloat(item.price || 0);
              return (
                <tr key={item.id} className="border-b border-gray-100">
                  <td className="py-3 text-sm text-gray-500">{String(index + 1).padStart(2, '0')}</td>
                  <td className="py-3 text-gray-900">{item.name}</td>
                  <td className="py-3 text-center text-gray-900">{item.quantity}</td>
                  <td className="py-3 text-center text-gray-600">{item.unit || 'пар.'}</td>
                  {documentType === 'invoice' && (
                    <>
                      <td className="py-3 text-right text-gray-900">{parseFloat(item.price || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td className={`py-3 text-right font-semibold text-green-700`}>{itemTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totals - Only for Invoice */}
      {documentType === 'invoice' && (
        <div className="flex justify-end mb-8">
          <div className="w-64">
            <div className="flex justify-between py-2 text-gray-600">
              <span>Без ДДВ</span>
              <span>{subtotal.toLocaleString('en-US', { minimumFractionDigits: 2 })} {invoice.currency}.</span>
            </div>
            <div className="flex justify-between py-2 text-gray-600">
              <span>ДДВ (18%)</span>
              <span>{vat.toLocaleString('en-US', { minimumFractionDigits: 2 })} {invoice.currency}.</span>
            </div>
            <div className="flex justify-between py-3 text-xl font-bold border-t-2 border-gray-200">
              <span>Вкупно</span>
              <span className='text-green-700'>{total.toLocaleString('en-US', { minimumFractionDigits: 2 })} {invoice.currency}.</span>
            </div>
          </div>
        </div>
      )}

      {/* Notes */}
      {invoice.notes && (
        <div className="mb-8">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">ЗАБЕЛЕШКИ</div>
          <div className="bg-gray-50 p-4 text-sm text-gray-700">
            {invoice.notes}
          </div>
        </div>
      )}

      {/* Spacer to push signatures to bottom */}
      <div className="flex-grow"></div>

      {/* Signatures */}
      <div className="grid grid-cols-2 gap-8 pt-8 items-end">
        <div className="text-center">
          <div className="border-t border-gray-300 pt-2 mx-8">
            <span className="text-xs text-gray-500 uppercase tracking-wider">Издал</span>
          </div>
        </div>
        <div className="text-center">
          <div className="mb-2 text-xs text-gray-500 text-left mx-8">
            {'__.__. ' + (invoice.date ? invoice.date.slice(0, 4) : '____')}
          </div>
          <div className="border-t border-gray-300 pt-2 mx-8">
            <span className="text-xs text-gray-500 uppercase tracking-wider">Примил</span>
          </div>
        </div>
      </div>

      {/* Footer - Contact Details and QR Code */}
      <div className="pt-4 border-t border-gray-100 flex justify-between items-start">
        <div className="text-xs text-gray-400 space-y-1">
          {businessDetails.website && <div>{businessDetails.website}</div>}
          {businessDetails.email && <div>{businessDetails.email}</div>}
          {businessDetails.phone && <div>{businessDetails.phone}</div>}
        </div>
        <div className="flex justify-center">
          <QRCodeSVG value={invoice.invoiceNumber || ' '} size={96} />
        </div>
      </div>
    </div>
  );
};

export default InvoiceA4Document;
